(() => {
  if (customElements.get("subscription-frequency-estimator")) return;

  class SubscriptionFrequencyEstimator extends HTMLElement {
    constructor() {
      super();

      this.result = null;
      this.openButton = null;
      this.modal = null;
      this.range = null;
      this.bubble = null;
      this.rangeWrap = null;
      this.qtyOutput = null;
      this.frequencyOutput = null;
      this.packLabel = null;
      this.saveButton = null;
      this.isDraggingBubble = false;

      this.onKeydown = this.onKeydown.bind(this);
      this.onRechargeUpdate = () =>
        requestAnimationFrame(() => {
          this.placeTrigger();
          this.update();
        });
    }

    connectedCallback() {
      this.openButton = this.querySelector(".subscription-frequency-estimator__trigger");
      this.modal = this.querySelector(".subscription-frequency-estimator__modal");
      this.range = this.querySelector(".subscription-frequency-estimator__range");
      this.bubble = this.querySelector(".subscription-frequency-estimator__bubble");
      this.rangeWrap = this.querySelector(".subscription-frequency-estimator__range-wrap");
      this.qtyOutput = this.querySelector("[data-estimator-qty]");
      this.frequencyOutput = this.querySelector("[data-estimator-frequency]");
      this.packLabel = this.querySelector("[data-estimator-pack-label]");
      this.saveButton = this.querySelector(".subscription-frequency-estimator__save");

      if (!this.openButton || !this.modal || !this.range || !this.saveButton) return;

      this.openButton.addEventListener("click", () => this.open());
      this.querySelectorAll("[data-estimator-close]").forEach((button) => {
        button.addEventListener("click", () => this.close());
      });
      this.range.addEventListener("input", () => this.update());
      this.bindBubbleDrag();
      this.saveButton.addEventListener("click", () => this.save());
      document.addEventListener("Recharge:Widget:Loaded", this.onRechargeUpdate);
      document.addEventListener("Recharge:Widget:Enable", this.onRechargeUpdate);

      this.normalizeRange();
      this.placeTrigger();
      this.update();
    }

    disconnectedCallback() {
      document.removeEventListener("keydown", this.onKeydown);
      document.removeEventListener("Recharge:Widget:Loaded", this.onRechargeUpdate);
      document.removeEventListener("Recharge:Widget:Enable", this.onRechargeUpdate);
      this.unbindBubbleDrag();
    }

    bindBubbleDrag() {
      if (!this.bubble) return;

      this.onBubblePointerDown = (event) => {
        event.preventDefault();
        this.isDraggingBubble = true;
        this.bubble.setPointerCapture(event.pointerId);
        this.bubble.classList.add("is-dragging");
        this.setValueFromPointer(event.clientX);
      };

      this.onBubblePointerMove = (event) => {
        if (!this.isDraggingBubble) return;
        this.setValueFromPointer(event.clientX);
      };

      this.onBubblePointerUp = (event) => {
        if (!this.isDraggingBubble) return;
        this.isDraggingBubble = false;
        this.bubble.classList.remove("is-dragging");
        if (this.bubble.hasPointerCapture(event.pointerId)) {
          this.bubble.releasePointerCapture(event.pointerId);
        }
      };

      this.bubble.addEventListener("pointerdown", this.onBubblePointerDown);
      this.bubble.addEventListener("pointermove", this.onBubblePointerMove);
      this.bubble.addEventListener("pointerup", this.onBubblePointerUp);
      this.bubble.addEventListener("pointercancel", this.onBubblePointerUp);
    }

    unbindBubbleDrag() {
      if (!this.bubble || !this.onBubblePointerDown) return;

      this.bubble.removeEventListener("pointerdown", this.onBubblePointerDown);
      this.bubble.removeEventListener("pointermove", this.onBubblePointerMove);
      this.bubble.removeEventListener("pointerup", this.onBubblePointerUp);
      this.bubble.removeEventListener("pointercancel", this.onBubblePointerUp);
    }

    setValueFromPointer(clientX) {
      const rect = this.range.getBoundingClientRect();
      if (!rect.width) return;

      const min = Number(this.range.min);
      const max = Number(this.range.max);
      const span = Math.max(max - min, 1);
      const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
      const value = Math.round(min + ratio * span);

      if (Number(this.range.value) === value) return;

      this.range.value = value;
      this.range.dispatchEvent(new Event("input", { bubbles: true }));
      this.update();
    }

    placeTrigger() {
      const deliveryFrequency = document.querySelector("recharge-widget .delivery-frequency");
      const heading = deliveryFrequency?.querySelector(":scope > h3");

      if (!deliveryFrequency || !heading || !this.openButton) return;

      let header = deliveryFrequency.querySelector(
        ":scope > .subscription-frequency-estimator__delivery-header"
      );

      if (!header) {
        header = document.createElement("div");
        header.className = "subscription-frequency-estimator__delivery-header";
        deliveryFrequency.insertBefore(header, heading);
        header.appendChild(heading);
      }

      let prompt = header.querySelector(".subscription-frequency-estimator__delivery-prompt");

      if (!prompt) {
        prompt = document.createElement("span");
        prompt.className = "subscription-frequency-estimator__delivery-prompt";
        prompt.textContent = "Find your perfect delivery frequency:";
        header.appendChild(prompt);
      }

      prompt.appendChild(this.openButton);
    }

    normalizeRange() {
      const min = this.getNumber("minCups", 1);
      const max = Math.max(min, this.getNumber("maxCups", 15));
      const fallbackDefault = Math.min(Math.max(this.getNumber("defaultCups", min), min), max);

      this.range.min = min;
      this.range.max = max;
      this.range.value = fallbackDefault;
    }

    getNumber(key, fallback) {
      const value = Number(this.dataset[key]);
      return Number.isFinite(value) && value > 0 ? value : fallback;
    }

    open() {
      this.modal.hidden = false;
      document.addEventListener("keydown", this.onKeydown);
      this.update();
      requestAnimationFrame(() => {
        this.range.focus({ preventScroll: true });
      });
    }

    close() {
      this.modal.hidden = true;
      document.removeEventListener("keydown", this.onKeydown);
      this.openButton.focus();
    }

    onKeydown(event) {
      if (event.key === "Escape") {
        this.close();
      }
    }

    update() {
      const cupsPerDay = Number(this.range.value);
      const min = Number(this.range.min);
      const max = Number(this.range.max);
      const rangeSpan = Math.max(max - min, 1);
      const progress = ((cupsPerDay - min) / rangeSpan) * 100;

      this.style.setProperty("--estimator-progress", `${progress}%`);
      this.querySelector(".subscription-frequency-estimator__range-wrap")?.style.setProperty(
        "--estimator-progress",
        `${progress}%`
      );

      if (this.bubble) {
        this.bubble.textContent = cupsPerDay;
        this.bubble.dataset.value = String(cupsPerDay);
      }

      this.result = this.calculate(cupsPerDay);

      if (!this.result) {
        this.qtyOutput.textContent = "--";
        this.frequencyOutput.textContent = "--";
        this.saveButton.disabled = true;
        return;
      }

      this.qtyOutput.textContent = this.result.quantity;
      this.frequencyOutput.textContent = this.result.label;
      this.packLabel.textContent = this.result.quantity === 1 ? "Pack" : "Packs";
      this.saveButton.disabled = false;
    }

    calculate(cupsPerDay) {
      const cupsPerPack = this.getNumber("cupsPerPack", 0);
      const plans = this.getRechargePlans();

      if (!cupsPerPack || !plans.length) return null;

      let targetDays = 14;
      if (cupsPerDay <= 2) {
        targetDays = 60;
      } else if (cupsPerDay <= 5) {
        targetDays = 30;
      }

      const recommendedPlan = [...plans].sort(
        (a, b) => Math.abs(a.days - targetDays) - Math.abs(b.days - targetDays)
      )[0];

      return {
        ...recommendedPlan,
        quantity: Math.ceil((cupsPerDay * recommendedPlan.days) / cupsPerPack),
      };
    }

    getRechargePlans() {
      const select = document.querySelector("recharge-widget .delivery-frequency-dropdown select");
      if (!select) return [];

      return [...select.options]
        .map((option) => {
          const days = this.parseFrequencyDays(option.textContent);
          if (!option.value || !days) return null;

          return {
            days,
            label: this.formatFrequencyLabel(option.textContent),
            value: option.value,
          };
        })
        .filter(Boolean);
    }

    parseFrequencyDays(label) {
      const normalized = label.trim().toLowerCase();
      const match = normalized.match(/every\s+(\d+)?\s*(week|weeks|month|months|year|years)/);
      if (!match) return null;

      const count = Number(match[1] || 1);
      const unit = match[2];

      if (unit.startsWith("week")) return count * 7;
      if (unit.startsWith("month")) return count * 30;
      if (unit.startsWith("year")) return count * 365;

      return null;
    }

    formatFrequencyLabel(label) {
      return label
        .trim()
        .replace(/^delivery\s+/i, "")
        .replace(/^every/i, "Every");
    }

    save() {
      this.update();
      if (!this.result) return;

      this.selectSubscription();
      this.applyQuantity(this.result.quantity);
      this.applySellingPlan(this.result.value);
      this.close();
    }

    selectSubscription() {
      const subscriptionCheckbox = document.querySelector("recharge-widget #subscription_checkbox");
      const oneTimeCheckbox = document.querySelector("recharge-widget #onetime_checkbox");

      if (subscriptionCheckbox && !subscriptionCheckbox.checked) {
        subscriptionCheckbox.click();
      }

      if (oneTimeCheckbox && oneTimeCheckbox.checked) {
        oneTimeCheckbox.checked = false;
      }
    }

    applyQuantity(quantity) {
      const quantityInput = document.querySelector(".product-form__quantity .quantity__input");
      if (!quantityInput) return;

      quantityInput.value = quantity;
      quantityInput.dispatchEvent(new Event("input", { bubbles: true }));
      quantityInput.dispatchEvent(new Event("change", { bubbles: true }));
    }

    applySellingPlan(value) {
      const select = document.querySelector("recharge-widget .delivery-frequency-dropdown select");
      if (!select) return;

      select.value = value;
      select.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }

  customElements.define("subscription-frequency-estimator", SubscriptionFrequencyEstimator);
})();
